/**
 * Promise-based access to the app's themed confirm dialog (GlobalConfirmModal),
 * for TS modules and inline scripts that need a yes/no answer before a
 * destructive action. Never use window.confirm — CLAUDE.md "Destructive
 * confirms"; enforced by scripts/lint_window_confirm.sh.
 *
 * Drives the imperative bridge registered in layout.templ's <head>: it listens
 * for `studio-confirm` on document with detail
 * `{ message, onConfirm, onCancel }`, marks `detail.handled = true` when it
 * takes the request, and calls exactly one of the callbacks (OK → onConfirm;
 * Cancel / Escape / a superseding confirm / dialog failure → onCancel).
 *
 * Unlike window.confirm this is ASYNCHRONOUS: callers must `await` it and
 * re-check any state that could have changed while the dialog was open.
 *
 * Fails CLOSED: with no document or no bridge to show the dialog it resolves
 * false, so a destructive action is never issued unconfirmed.
 */
export const STUDIO_CONFIRM_EVENT = "studio-confirm";

export function studioConfirm(message: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const settle = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    if (typeof document === "undefined") {
      settle(false);
      return;
    }
    const detail: {
      message: string;
      onConfirm: () => void;
      onCancel: () => void;
      handled?: boolean;
    } = {
      message,
      onConfirm: () => settle(true),
      onCancel: () => settle(false),
    };
    // dispatchEvent is synchronous: by the time it returns the bridge has
    // either taken the request (handled) or there is none.
    document.dispatchEvent(new CustomEvent(STUDIO_CONFIRM_EVENT, { detail }));
    if (!detail.handled) {
      console.warn("[studioConfirm] no themed confirm bridge; treating as cancel");
      settle(false);
    }
  });
}
